/**
 * Platform DEFAULTS for plan-level fields (F1) — the bottom rung of the
 * inheritance chain **platform-default → plan → tenant-override**.
 *
 * A plan value left unset inherits the platform default; the plan edit modal
 * shows that default as a PLACEHOLDER and offers "Reset to default <value>".
 * This resolver is the ONE implementation of "what is the default for field X",
 * so the modal placeholder and any enforcement fallback cannot disagree.
 *
 * ── Read-only for now, by design ─────────────────────────────────────────
 *
 * `resolvePlanFieldDefaults` takes an OPTIONAL `stored` override so the future
 * "edit platform defaults" Price Book section slots in without touching any call
 * site: today every caller passes nothing and gets the shipped constants. When
 * the write path lands, the stored values come from nullable `platform_settings`
 * columns and the full FOUR-edit discipline (migration+journal, schema+CHECK,
 * `PATCH_KEYS`, PUT validation + audit subject) applies THERE. Nothing writes
 * these yet, so no column is added in this phase.
 *
 * ── Where the numbers come from ──────────────────────────────────────────
 *
 * Storage / concurrency reuse the existing `@papan/types` entitlement defaults.
 * Included-seats defaults to 0 (a plan grants seats explicitly). The per-tier
 * request-rate-limit defaults are the SHIPPED limiter defaults (DB 600 / SDK 300
 * / SCRIPTS 60 req·min⁻¹); the authoritative runtime values live in the API
 * limiter (`apps/api/src/middleware/rate-limit.ts`, env-overridable). These are
 * the shipped defaults, shown as a placeholder — a deployment that overrides the
 * `RL_*` env vars can diverge until the edit-defaults section makes the stored
 * value authoritative. TODO(follow-up): pin these against the limiter defaults
 * the way `V1_PRE_MAX_DEFAULT` is pinned, to remove the drift risk.
 */

import { DEFAULT_STORAGE_QUOTA_BYTES, DEFAULT_AGENT_CONCURRENCY_CAP } from './entitlement-defaults.js'
import { PLAN_RATE_LIMIT_TIERS, type PlanRateLimitTier } from './plan-rate-limits.js'

export type PlanRateLimitDefaults = Record<PlanRateLimitTier, number>

/**
 * Shipped per-tier request rate limits (req·min⁻¹), mirroring the limiter's env
 * defaults `RL_DB_MAX` / `RL_SDK_MAX` / `RL_SCRIPTS_MAX`. Keep in step with
 * `apps/api/src/middleware/rate-limit.ts`.
 */
export const PLAN_RATE_LIMIT_PLATFORM_DEFAULTS: PlanRateLimitDefaults = {
  db: 600,
  sdk: 300,
  scripts: 60,
}

export interface PlanFieldDefaults {
  storageQuotaBytes: number
  agentConcurrencyCap: number
  includedSeats: number
  rateLimits: PlanRateLimitDefaults
}

/** Included seats a plan grants when unset — none; a plan states its own. */
export const DEFAULT_PLAN_INCLUDED_SEATS = 0

/**
 * `billing_plans.fairness_weight`'s column default (#539, 0210) — the O7
 * relative compute share a plan carries until an operator changes it.
 *
 * Deliberately NOT in `PlanFieldDefaults`: that describes values a future
 * platform-defaults editor may override per deployment, and this is the
 * column's own NOT NULL default, which the migration already applies to every
 * row. It exists here only so the SPA can label the field and seed a new
 * plan's draft without a bare literal, and because `@papan/sandbox`'s
 * `DEFAULT_WEIGHT` is a different fact — the WORKER's fallback when no plan row
 * resolves at all — that the browser has no business importing.
 *
 * The two are the same number on purpose: a tenant whose plan cannot be
 * resolved must not be treated differently from one on an unconfigured plan.
 */
export const DEFAULT_PLAN_FAIRNESS_WEIGHT = 1

/** The range `billing_plans_fairness_weight_range` (0210) enforces, mirrored
 *  for the editor's own validation so a bad value is refused before the round
 *  trip rather than surfacing as an opaque server error. */
export const PLAN_FAIRNESS_WEIGHT_MIN = 1
export const PLAN_FAIRNESS_WEIGHT_MAX = 100

/**
 * A stored platform-defaults override (future DB layer). Every field is
 * optional and nullable with the standard split: `undefined`/`null` = "not
 * chosen, use the constant"; a real value (including `0`) is honoured.
 */
export interface StoredPlanFieldDefaults {
  storageQuotaBytes?: number | null
  agentConcurrencyCap?: number | null
  includedSeats?: number | null
  rateLimits?: Partial<PlanRateLimitDefaults> | null
}

/** `stored value if a real value was chosen, else the constant`. `0` is real. */
function pick(stored: number | null | undefined, fallback: number): number {
  return stored != null ? stored : fallback
}

/**
 * Resolve the platform defaults for every plan-level field, overlaying any
 * `stored` values on the shipped constants. Pure and browser-safe (no DB, no
 * env) so the modal can call it directly.
 */
export function resolvePlanFieldDefaults(stored?: StoredPlanFieldDefaults | null): PlanFieldDefaults {
  const rateLimits = { ...PLAN_RATE_LIMIT_PLATFORM_DEFAULTS }
  if (stored?.rateLimits) {
    for (const tier of PLAN_RATE_LIMIT_TIERS) {
      const v = stored.rateLimits[tier]
      if (v != null) rateLimits[tier] = v
    }
  }
  return {
    storageQuotaBytes: pick(stored?.storageQuotaBytes, DEFAULT_STORAGE_QUOTA_BYTES),
    agentConcurrencyCap: pick(stored?.agentConcurrencyCap, DEFAULT_AGENT_CONCURRENCY_CAP),
    includedSeats: pick(stored?.includedSeats, DEFAULT_PLAN_INCLUDED_SEATS),
    rateLimits,
  }
}
